import { Chart, registerables } from 'chart.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface Props {
  location: string;
  geofips: number | string | null;
  preferredState?: string | null;
  yearStart?: number;
  yearEnd?: number;
}

type Row = {
  geofips: number;
  location_name: string;
  county: string | null;
  state: string;
  geography_type: 'state' | 'county' | string;
  year: number;
  means_of_transportation: string;
  estimate: number;
  margin_of_error: number | null;
  percent: number | null;
  percentile: number | null;
};

const normalizeMode = (value: string) =>
  value
    .toLowerCase()
    .replace(/[(),:]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const MeansOfTransportation: React.FC<Props> = ({
  location,
  geofips,
  preferredState = null,
  yearStart = 2009,
  yearEnd = 2023
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countyRows, setCountyRows] = useState<Row[]>([]);
  const [activeView, setActiveView] = useState<string>('overview');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  const modeConfig = useMemo(
    () => [
      { key: 'car', label: 'Car, Truck, or Van', color: '#ef4444', matches: ['car, truck, or van', 'car truck or van'] },
      { key: 'public_transit', label: 'Public Transit', color: '#3b82f6', matches: ['public transportation (excluding taxicab)', 'public transportation', 'public transit'] },
      { key: 'bicycle', label: 'Bicycle', color: '#10b981', matches: ['bicycle', 'bike', 'biked'] },
      { key: 'walked', label: 'Walked', color: '#f59e0b', matches: ['walked', 'walking'] },
      { key: 'work_from_home', label: 'Worked from Home', color: '#8b5cf6', matches: ['worked from home', 'work from home'] },
      { key: 'other_means', label: 'Other Means', color: '#6b7280', matches: ['other means', 'other means of transportation', 'other'] },
      { key: 'taxicab', label: 'Taxicab', color: '#a855f7', matches: ['taxicab', 'taxi'] },
      { key: 'motorcycle', label: 'Motorcycle', color: '#ec4899', matches: ['motorcycle', 'motorbike'] }
    ],
    []
  );

  const modeKeyByNormalized = useMemo(() => {
    const map = new Map<string, string>();
    modeConfig.forEach((mode) => {
      mode.matches.forEach((match) => {
        map.set(normalizeMode(match), mode.key);
      });
    });
    return map;
  }, [modeConfig]);

  const getModeKey = useCallback(
    (value: string) => modeKeyByNormalized.get(normalizeMode(value)),
    [modeKeyByNormalized]
  );

  const rowsByModeAndYear = useMemo(() => {
    const map = new Map<string, Map<number, Row>>();
    countyRows.forEach((row) => {
      const key = getModeKey(row.means_of_transportation);
      if (!key) return;
      const yearMap = map.get(key) || new Map<number, Row>();
      yearMap.set(row.year, row);
      map.set(key, yearMap);
    });
    return map;
  }, [countyRows, getModeKey]);

  const totalEstimateByYear = useMemo(() => {
    const totals = new Map<number, number>();
    countyRows.forEach((row) => {
      if (row.estimate == null) return;
      const current = totals.get(row.year) || 0;
      totals.set(row.year, current + row.estimate);
    });
    return totals;
  }, [countyRows]);

  const getRowPercent = useCallback((row?: Row | null) => {
    if (!row) return null;
    if (row.percent != null) return row.percent;
    const total = totalEstimateByYear.get(row.year) || 0;
    if (row.estimate != null && total > 0) {
      return (row.estimate / total) * 100;
    }
    return null;
  }, [totalEstimateByYear]);

  useEffect(() => {
    const fetchData = async () => {
      // Add early return if location is not set
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A county geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching means_of_transportation_to_work data for location:', location, 'geofips:', geofips);
        console.log('Year range:', yearStart, 'to', yearEnd);
        
        let allData = null;
        let allErr = null;
        
        // Try place (city) data first, then county, then state
        // Prefer geofips (unambiguous); location ilike matches "South San Francisco" when selecting "San Francisco"
        let query = supabaseClient
          .from('means_of_transportation_to_work')
          .select('*')
          .eq('geography_type', 'place')
          .gte('year', yearStart)
          .lte('year', yearEnd)
          .order('year', { ascending: true })
          .order('means_of_transportation', { ascending: true });
        
        if (geofips) {
          const geofipsNum = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
          if (!Number.isNaN(geofipsNum)) query = query.eq('geofips', geofipsNum);
        }
        if (!geofips && location) {
          query = query.eq('location_name', location);
        }
        
        const { data: placeData, error: placeErr } = await query;
        
        if (placeErr) {
          console.error('Error fetching place data:', placeErr);
        }
        
        console.log('Place query result:', placeData?.length || 0, 'rows');
        if (placeData && placeData.length > 0) {
          console.log('Found place (city) data:', placeData.length, 'rows');
          console.log('Sample place data:', placeData[0]);
          allData = placeData;
        } else {
          console.log('No place data found, trying county data...');
          
          // Try county data
          let countyQuery = supabaseClient
            .from('means_of_transportation_to_work')
            .select('*')
            .eq('geography_type', 'county')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true })
            .order('means_of_transportation', { ascending: true });
          
          if (geofips) {
            const g = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
            if (!Number.isNaN(g)) countyQuery = countyQuery.eq('geofips', g);
          }
          if (!geofips && location) {
            countyQuery = countyQuery.eq('location_name', location);
          }
          
          const { data: countyData, error: countyErr } = await countyQuery;
          
          if (countyErr) {
            console.error('Error fetching county data:', countyErr);
          }
          
          console.log('County query result:', countyData?.length || 0, 'rows');
          if (countyData && countyData.length > 0) {
            console.log('Found county data:', countyData.length, 'rows');
            console.log('Sample county data:', countyData[0]);
            allData = countyData;
          } else {
            console.log('No county data found, trying state data...');
            
            // Try state data
            let stateQuery = supabaseClient
              .from('means_of_transportation_to_work')
              .select('*')
              .eq('geography_type', 'state')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true })
              .order('means_of_transportation', { ascending: true });
            
            if (geofips) {
              const g = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
              if (!Number.isNaN(g)) stateQuery = stateQuery.eq('geofips', g);
            }
            if (!geofips && location) {
              stateQuery = stateQuery.eq('location_name', location);
            }
            
            const { data: stateData, error: stateErr } = await stateQuery;
            
            if (stateErr) {
              console.error('Error fetching state data:', stateErr);
            }
            
            console.log('State query result:', stateData?.length || 0, 'rows');
            if (stateData && stateData.length > 0) {
              console.log('Found state-level data:', stateData.length, 'rows');
              console.log('Sample state data:', stateData[0]);
              allData = stateData;
            } else {
              console.log('No data found for any geography type');
              allData = [];
            }
            allErr = stateErr; // Assign the last error encountered
          }
        }

        console.log('Found data:', allData?.length || 0, 'rows for location:', location);
        if (allData && allData.length > 0) {
          console.log('Sample data:', allData[0]);
        }

        if (allErr) {
          console.error('Error fetching data:', allErr);
          throw allErr;
        }

        console.log('Total data found:', allData?.length || 0, 'rows');
        if (allData && allData.length > 0) {
          console.log('Sample data:', allData[0]);
          console.log('Geography types:', Array.from(new Set(allData.map(r => r.geography_type))));
        }
        
        // Use state data if this is a state (geofips 6 = California)
        // Otherwise use county data, but if we found place data, use that
        let cData = allData && allData.length > 0 
          ? (allData[0].geography_type === 'state' ? allData : 
             allData[0].geography_type === 'place' ? allData : 
             allData.filter(r => r.geography_type === 'county'))
          : [];
        
        // If no data found, try county-level data as fallback (but only if location is explicitly a city)
        if (cData.length === 0 && allData.length > 0) {
          const cityData = allData.find(r => r.geography_type === 'City' || r.geography_type === 'place');
          if (cityData) {
            // Try to get county name from the city data
            const countyName = cityData.county;
            if (countyName) {
              console.log(`No city data found for ${location}, trying ${countyName} County fallback...`);
              
              const { data: countyData, error: countyErr } = await supabaseClient
                .from('means_of_transportation_to_work')
                .select('*')
                .ilike('location_name', `%${countyName}%`)
                .eq('geography_type', 'County')
                .gte('year', yearStart)
                .lte('year', yearEnd)
                .order('year', { ascending: true })
                .order('means_of_transportation', { ascending: true });
          
              if (countyErr) {
                console.error(`Error fetching ${countyName} County data:`, countyErr);
              } else {
                console.log(`${countyName} County data found:`, countyData?.length || 0, 'rows');
                cData = countyData || [];
              }
            }
          }
        }
        
        // Dedupe: if multiple locations per (means_of_transportation, year) - e.g. San Francisco + South San Francisco - prefer exact location match
        let finalRows = cData || [];
        if (finalRows.length > 0 && location) {
          const byModeYear = new Map<string, Row[]>();
          for (const r of finalRows) {
            const key = `${r.means_of_transportation}__${r.year}`;
            const list = byModeYear.get(key) || [];
            list.push(r as Row);
            byModeYear.set(key, list);
          }
          finalRows = Array.from(byModeYear.values()).flatMap((list) => {
            if (list.length === 1) return list;
            const exact = list.find((r) => r.location_name === location);
            return exact ? [exact] : [list[0]];
          });
        }
        
        setCountyRows(finalRows);
        setLoading(false);
      } catch (e: any) {
        console.error('Error loading means_of_transportation_to_work:', e);
        setError(e.message || 'Failed to load means of transportation data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || countyRows.length === 0) return null;
    
    const baselineYear = 2019;

    // Find car/truck/van data for baseline year and latest year
    const carData2019 = countyRows.find(row => row.year === baselineYear && getModeKey(row.means_of_transportation) === 'car');
    
    const latestYear = Math.max(...countyRows.map(r => r.year));
    const carDataLatest = countyRows.find(row => row.year === latestYear && getModeKey(row.means_of_transportation) === 'car');
    
    // Find work from home data for baseline year and latest year
    const wfhData2019 = countyRows.find(row => row.year === baselineYear && getModeKey(row.means_of_transportation) === 'work_from_home');
    const wfhDataLatest = countyRows.find(row => row.year === latestYear && getModeKey(row.means_of_transportation) === 'work_from_home');
    
    // Find public transportation data for baseline year and latest year
    const transitData2019 = countyRows.find(row => row.year === baselineYear && getModeKey(row.means_of_transportation) === 'public_transit');
    const transitDataLatest = countyRows.find(row => row.year === latestYear && getModeKey(row.means_of_transportation) === 'public_transit');
    
    const car2019 = carData2019 ? getRowPercent(carData2019) : null;
    const carLatest = carDataLatest ? getRowPercent(carDataLatest) : null;
    const wfh2019 = wfhData2019 ? getRowPercent(wfhData2019) : null;
    const wfhLatest = wfhDataLatest ? getRowPercent(wfhDataLatest) : null;
    const transit2019 = transitData2019 ? getRowPercent(transitData2019) : null;
    const transitLatest = transitDataLatest ? getRowPercent(transitDataLatest) : null;

    const carPointChange = car2019 != null && carLatest != null ? carLatest - car2019 : null;
    const wfhPointChange = wfh2019 != null && wfhLatest != null ? wfhLatest - wfh2019 : null;
    const transitPointChange = transit2019 != null && transitLatest != null ? transitLatest - transit2019 : null;

    const carRelativeChange = car2019 ? ((carPointChange || 0) / car2019) * 100 : null;
    const wfhRelativeChange = wfh2019 ? ((wfhPointChange || 0) / wfh2019) * 100 : null;
    const transitRelativeChange = transit2019 ? ((transitPointChange || 0) / transit2019) * 100 : null;
    
    return {
      baselineYear,
      car2019,
      carLatest,
      wfh2019,
      wfhLatest,
      transit2019,
      transitLatest,
      carPointChange,
      wfhPointChange,
      transitPointChange,
      carRelativeChange,
      wfhRelativeChange,
      transitRelativeChange,
      latestYear: latestYear
    };
  }, [loading, countyRows, getModeKey, getRowPercent]);

  // Build the chart after data loads
  useEffect(() => {
    if (loading || countyRows.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Prepare data for each mode - normalized structure with one row per mode per year
    console.log('Building chart with data:', countyRows.length, 'rows');
    if (countyRows.length > 0) {
      console.log('First row:', countyRows[0]);
      const uniqueModesSet = new Set(countyRows.map(r => r.means_of_transportation));
      console.log('Unique modes:', Array.from(uniqueModesSet));
    }
    
    const datasets = modeConfig.map(mode => {
      const yearMap = rowsByModeAndYear.get(mode.key);
      const data = years.map(year => {
        const row = yearMap?.get(year);
        const percent = getRowPercent(row);
        return percent != null ? percent : null;
      });

      return {
        label: mode.label,
        data: data,
        borderColor: mode.color,
        backgroundColor: mode.color + '33',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      };
    });

    const chartTitle = `Means of Transportation to Work - ${location || 'Selected Location'}`;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: '% of Workers' },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              callback: (v: any) => `${v}%`
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: standardTitle(chartTitle),
          legend: standardLegend,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toFixed(1) : 'N/A'}%`;
              }
            }
          }
        }
      }
    });
  }, [loading, years, countyRows, location, modeConfig, rowsByModeAndYear, getRowPercent]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Means_of_Transportation');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows: Array<Record<string, any>> = countyRows.map(row => ({
      year: row.year,
      location: row.location_name,
      county: row.county,
      state: row.state,
      geography_type: row.geography_type,
      means_of_transportation: row.means_of_transportation,
      estimate: row.estimate,
      margin_of_error: row.margin_of_error,
      percent: row.percent,
      percentile: row.percentile
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Means_of_Transportation_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading means of transportation data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  // If no location is set or no data found, don't show anything
  if (!location || (!loading && countyRows.length === 0)) {
    return null;
  }

  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const keyMetricsDisplay = keyMetrics ? (
    <div className="key-metrics-container">
      <div className="key-metric-item">
        <div className="key-metric-value">
          {keyMetrics.carPointChange != null ? `${keyMetrics.carPointChange > 0 ? '+' : ''}${keyMetrics.carPointChange.toFixed(1)} pts` : 'N/A'}
        </div>
        <div className="key-metric-label">Car/Truck/Van Change Since 2019 (pts)</div>
      </div>
      <div className="key-metric-item">
        <div className="key-metric-value">
          {keyMetrics.wfhPointChange != null ? `${keyMetrics.wfhPointChange > 0 ? '+' : ''}${keyMetrics.wfhPointChange.toFixed(1)} pts` : 'N/A'}
        </div>
        <div className="key-metric-label">Work From Home Change Since 2019 (pts)</div>
      </div>
      <div className="key-metric-item">
        <div className="key-metric-value">{keyMetrics.latestYear}</div>
        <div className="key-metric-label">Latest Year</div>
      </div>
    </div>
  ) : null;

  return (
    <ChartLayout
      title="Means of Transportation to Work"
      key={`means-transportation-${location}`}
      description={`${location} • Source: American Community Survey (ACS) 5-Year Estimates`}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      chartSelector={undefined}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Means_of_Transportation')}
      csvFilename={formatFilename(location, 'Means_of_Transportation_Data')}
      enableComments={true}
      location={location}
      overviewContent={
        <OverviewContent
          title="Transportation Mode Analysis"
          description="This chart shows how workers commute to their jobs across different transportation modes over time. Understanding these patterns is essential for climate planning and transportation policy."
        >
          <div>
            {keyMetrics ? (
              <div>
                <p>
                  {keyMetrics.car2019 != null && keyMetrics.carLatest != null && keyMetrics.carPointChange != null ? (
                    <>
                      Car, truck, or van commuting {keyMetrics.carPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(keyMetrics.carPointChange).toFixed(1)} percentage points
                      (from {keyMetrics.car2019.toFixed(1)}% to {keyMetrics.carLatest.toFixed(1)}%){keyMetrics.carRelativeChange != null ? `, a ${Math.abs(keyMetrics.carRelativeChange).toFixed(0)}% relative change` : ''} since {keyMetrics.baselineYear}.
                    </>
                  ) : null}{' '}
                  {keyMetrics.transit2019 != null && keyMetrics.transitLatest != null && keyMetrics.transitPointChange != null ? (
                    <>
                      Public transit {keyMetrics.transitPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(keyMetrics.transitPointChange).toFixed(1)} percentage points
                      (from {keyMetrics.transit2019.toFixed(1)}% to {keyMetrics.transitLatest.toFixed(1)}%){keyMetrics.transitRelativeChange != null ? `, a ${Math.abs(keyMetrics.transitRelativeChange).toFixed(0)}% relative change` : ''} since {keyMetrics.baselineYear}.
                    </>
                  ) : null}{' '}
                  {keyMetrics.wfh2019 != null && keyMetrics.wfhLatest != null && keyMetrics.wfhPointChange != null ? (
                    <>
                      Worked from home {keyMetrics.wfhPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(keyMetrics.wfhPointChange).toFixed(1)} percentage points
                      (from {keyMetrics.wfh2019.toFixed(1)}% to {keyMetrics.wfhLatest.toFixed(1)}%){keyMetrics.wfhRelativeChange != null ? `, a ${Math.abs(keyMetrics.wfhRelativeChange).toFixed(0)}% relative change` : ''} since {keyMetrics.baselineYear}.
                    </>
                  ) : null}
                </p>
              </div>
            ) : (
              <div>
                <p><strong>Key Transportation Modes:</strong></p>
                <ul>
                  <li><strong>Car, Truck, or Van</strong> (typically 70-85% of commutes)</li>
                  <li><strong>Public Transit</strong> - buses, trains, subways</li>
                  <li><strong>Bicycle</strong> - active transportation</li>
                  <li><strong>Walked</strong> - pedestrian commuting</li>
                  <li><strong>Worked from Home</strong> - remote work</li>
                  <li><strong>Other Means</strong> - motorcycle, taxicab, etc.</li>
                </ul>
              </div>
            )}
          </div>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Policy Relevance"
          description="The means of transportation indicator reflects residents' travel behaviors, which are vital for shaping demand-side climate policies and reducing emissions."
        >
          <div>
            <p>It is likely strongly correlated with the availability of viable alternative transportation options. Analyzing these patterns allows local governments to:</p>
            <ul>
              <li>Identify high-emission modes and promote shifts to low-carbon alternatives</li>
              <li>Implement targeted actions like expanding public transit</li>
              <li>Enhance pedestrian and bike infrastructure</li>
              <li>Support electric vehicle adoption</li>
              <li>Inform land use and mobility planning to reduce vehicle miles traveled</li>
            </ul>
            <p>By aligning transportation data with climate goals, local governments can develop strategies that deliver environmental and social benefits, such as improved air quality, reduced congestion, better public health, and increased mobility for underserved communities.</p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="Access to low-carbon transportation options is not equally distributed across communities."
        >
          <div>
            <p><strong>Transportation Equity Challenges:</strong></p>
            <ul>
              <li>Low-income communities and communities of color often face limited access to public transit, biking, and walking infrastructure due to historic underinvestment and systemic barriers</li>
              <li>These groups are more likely to rely on public transit but often face longer commutes, safety issues, or poor service</li>
              <li>The option to work from home is not equally accessible for all job types, particularly for low-paid service jobs that are required to be on-site</li>
              <li>Climate policies that encourage shifts from car dependence must ensure alternatives are accessible and affordable to all</li>
            </ul>
            <p><strong>Without an equity lens</strong>, such policies risk deepening disparities by primarily benefiting wealthier households. Understanding travel behaviors across populations helps local governments design inclusive strategies that prioritize underserved communities and equitably deliver climate and health benefits.</p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="This analysis uses American Community Survey (ACS) 5-Year sample data, with the final year of the sample period representing the current reporting year."
          dataSources={[
            'American Community Survey (ACS) 5-Year Estimates',
            'ACS Table B08301: Means of Transportation to Work'
          ]}
          methodology={[
            'The percentage of workers by mode of transportation is calculated by dividing the number of workers using each transportation type by the total number of workers',
            'These values represent modal shares across all commuting methods reported in the ACS data',
            'Data filtered by county geofips for geographic specificity',
            'Time series analysis from 2010-2023 where data available',
            'Each mode shown as a percentage of total workers to enable comparison across modes and over time'
          ]}
        />
      }
    />
  );
};

export default MeansOfTransportation;

